import React from "react";
import { Link } from "react-router-dom";
import { useGetBusinessesQuery, useGetLicensesQuery } from "../../store/api";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Table from "../../components/common/Table";
import { computeIsExpired, getCurrentLicense } from "../../utils/license";

const BusinessActions = ({ businessId }) => {
  const { data: licenseData, isLoading, isFetching, isError } =
    useGetLicensesQuery(businessId, { skip: !businessId });

  const currentLicense = getCurrentLicense(licenseData);
  const hasLicense =
    Boolean(currentLicense) &&
    !(typeof currentLicense === "object" && Object.keys(currentLicense).length === 0);
  const status = String(currentLicense?.status || "").trim().toUpperCase();
  const isStatusInactive = Boolean(status) && status !== "ACTIVE";
  const isExpired = hasLicense && (isStatusInactive || computeIsExpired(currentLicense));
  const isActive = hasLicense && !isExpired;

  return (
    <div className="flex flex-wrap gap-3 text-sm">
      <Link className="font-semibold text-[#4f46e5]" to={`/businesses/${businessId}`}>
        View
      </Link>
      <Link
        className="font-semibold text-[#4f46e5]"
        to={`/businesses/${businessId}/licenses`}
      >
        Licenses
      </Link>

      {isLoading || isFetching ? (
        <span className="font-semibold text-slate-400">Checking...</span>
      ) : isError ? (
        <span className="font-semibold text-slate-400">License status unavailable</span>
      ) : isActive ? (
        <span className="font-semibold text-emerald-600">Allocated</span>
      ) : isExpired ? (
        <Link
          className="font-semibold text-[#4f46e5]"
          to={`/businesses/${businessId}/licenses/edit`}
        >
          Reallocate
        </Link>
      ) : (
        <Link
          className="font-semibold text-[#4f46e5]"
          to={`/businesses/${businessId}/licenses/new`}
        >
          Allocate
        </Link>
      )}

      <Link
        className="font-semibold text-[#4f46e5]"
        to={`/businesses/${businessId}/stores`}
      >
        Stores
      </Link>
    </div>
  );
};

const BusinessList = () => {
  const { data, isLoading, isError, error } = useGetBusinessesQuery();
  const businesses = Array.isArray(data) ? data : [];

  const columns = [
    { key: "name", label: "Business" },
    { key: "code", label: "Code" },
    { key: "contact", label: "Contact" },
    { key: "actions", label: "Actions" },
  ];

  const rows = businesses.map((business) => {
    const id = business.businessId || business.id;
    return {
      id,
      name: business.businessName || business.name,
      code: business.businessCode || business.code,
      contact: business.contactEmail || business.email,
      actions: <BusinessActions businessId={id} />,
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Businesses"
        subtitle="Manage tenants and licenses."
        actions={
          <Link to="/businesses/new">
            <Button size="sm">Create Business</Button>
          </Link>
        }
      />

      <Card className="p-0">
        <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">
          Business Directory
        </div>
        <div className="px-6 py-4">
          {isLoading && (
            <p className="text-sm text-slate-500">Loading businesses...</p>
          )}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to load businesses."}
            </Alert>
          )}
          {!isLoading && !isError && (
            <Table
              columns={columns}
              rows={rows}
              emptyText="No businesses yet."
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default BusinessList;
